import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { PhoneInput } from './PhoneInput';
import { QRDisplayModal } from './QRDisplayModal';
import { whatsappService } from '../services/whatsappService';
import { supabase } from '../../../integrations/supabase/client';
import { useAuth } from '../../../context/AuthContext';
import { Loader2, QrCode, Key, ArrowLeft } from 'lucide-react';

interface NewChannelModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const NuevoChannelModal: React.FC<NewChannelModalProps> = ({ open, onClose, onSuccess }) => {
    const { user } = useAuth();
    const [connectionType, setConnectionType] = useState<'business' | 'api' | null>(null);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [accessToken, setAccessToken] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrData, setQrData] = useState<{
        qrCode: string;
        instanceName: string;
        connectionId: string;
    } | null>(null);

    const resetState = () => {
        setConnectionType(null);
        setPhoneNumber('');
        setAccessToken('');
        setError('');
        setLoading(false);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleBack = () => {
        setConnectionType(null);
        setPhoneNumber('');
        setAccessToken('');
        setError('');
    };

    // --- WhatsApp Business (QR) flow ---
    const handleBusinessSubmit = async () => {
        if (!user) {
            setError('Debes iniciar sesión para agregar un canal');
            return;
        }

        setLoading(true);
        setError('');

        try {
            // 1. Get next auto-increment instance name
            const sessionName = await whatsappService.getNextInstanceName();

            // 2. Create WAHA session
            const result = await whatsappService.createSession(sessionName);

            // 3. If session was created as STOPPED, start it
            if (result.status === 'STOPPED') {
                await whatsappService.startSession(sessionName);
            }

            // 4. Wait for session to reach SCAN_QR_CODE (retry up to 8 times, ~16s total)
            let pollResult: Awaited<ReturnType<typeof whatsappService.pollSessionStatus>> | null = null;
            for (let attempt = 0; attempt < 8; attempt++) {
                await new Promise(resolve => setTimeout(resolve, 2000));
                pollResult = await whatsappService.pollSessionStatus(sessionName);
                if (pollResult.status === 'FAILED') {
                    throw new Error('La sesión falló al iniciar. Intenta de nuevo.');
                }
                if (pollResult.qrBase64 || pollResult.status === 'SCAN_QR_CODE' || pollResult.status === 'WORKING') {
                    break;
                }
            }
            if (!pollResult) {
                throw new Error('No se pudo obtener el estado de la sesión.');
            }

            // 6. Save pending connection (sessionName as temp numero_telefono for UNIQUE constraint)
            const savedConnection = await whatsappService.saveConnection({
                user_id: user.id,
                nombre_instancia: sessionName,
                numero_telefono: sessionName,
                api_key: '',
                qr_code_base64: pollResult.qrBase64 || '',
                estado: 'pending',
            });

            // 7. Open QR modal
            setQrData({
                qrCode: pollResult.qrBase64 || '',
                instanceName: sessionName,
                connectionId: savedConnection.id,
            });
            setShowQRModal(true);
        } catch (err: unknown) {
            console.error('Error al crear canal:', err);
            const message = err instanceof Error ? err.message : 'Error al crear el canal de WhatsApp. Intenta de nuevo.';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    // --- WhatsApp API flow (unchanged) ---
    const handleApiSubmit = async () => {
        if (!phoneNumber) {
            setError('Por favor ingresa un número de teléfono válido');
            return;
        }
        if (!accessToken.trim()) {
            setError('Por favor ingresa el Access Token');
            return;
        }
        if (!user) {
            setError('Debes iniciar sesión para agregar un canal');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const numeroSinPlus = phoneNumber.replace('+', '');

            const { data: existing } = await supabase
                .from('whatsapp_connections')
                .select('id')
                .eq('numero_telefono', numeroSinPlus)
                .maybeSingle();

            if (existing) {
                setError('Ya existe una conexión con este número');
                setLoading(false);
                return;
            }

            await whatsappService.saveConnection({
                user_id: user.id,
                nombre_instancia: accessToken.trim(),
                numero_telefono: numeroSinPlus,
                api_key: '',
                qr_code_base64: '',
                estado: 'connected',
            });

            resetState();
            onSuccess?.();
            onClose();
        } catch (err) {
            console.error('Error al crear canal API:', err);
            setError('Error al crear el canal. Por favor, intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    const handleQRSuccess = () => {
        setShowQRModal(false);
        resetState();
        onSuccess?.();
        onClose();
    };

    const handleQRClose = () => {
        setShowQRModal(false);
        setQrData(null);
    };

    if (!open) return null;

    // --- Step 1: Type Selection ---
    const renderTypeSelection = () => (
        <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
                Selecciona cómo deseas conectar tu canal de WhatsApp
            </p>
            <div className="grid grid-cols-2 gap-3">
                <button
                    onClick={() => setConnectionType('business')}
                    className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all cursor-pointer text-center"
                >
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                        <QrCode className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                        <p className="font-semibold text-sm text-foreground">WhatsApp Business</p>
                        <p className="text-xs text-muted-foreground mt-1">Conecta escaneando un código QR</p>
                    </div>
                </button>

                <button
                    onClick={() => setConnectionType('api')}
                    className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all cursor-pointer text-center"
                >
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <Key className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <p className="font-semibold text-sm text-foreground">WhatsApp API</p>
                        <p className="text-xs text-muted-foreground mt-1">Conecta con tu Access Token de GoHighLevel</p>
                    </div>
                </button>
            </div>
        </div>
    );

    // --- Step 2a: Business (QR) — No phone input needed ---
    const renderBusinessForm = () => (
        <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-900">
                    Se generará un código QR que deberás escanear desde tu WhatsApp para vincular tu número. El número se detectará automáticamente al completar la conexión.
                </p>
            </div>

            {error && (
                <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleBack} disabled={loading}>
                    <ArrowLeft className="w-4 h-4 mr-1" /> Volver
                </Button>
                <Button onClick={handleBusinessSubmit} disabled={loading}>
                    {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando QR...</>
                    ) : (
                        'Conectar WhatsApp'
                    )}
                </Button>
            </div>
        </div>
    );

    // --- Step 2b: API (Token) — Unchanged ---
    const renderApiForm = () => (
        <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-900">
                    Ingresa el número de WhatsApp y el Access Token de tu cuenta de GoHighLevel para conectar vía API.
                </p>
            </div>

            <PhoneInput value={phoneNumber} onChange={setPhoneNumber} error="" />

            <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">
                    Access Token
                </label>
                <input
                    type="text"
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                    placeholder="Ingresa tu Access Token de GoHighLevel"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 bg-background text-foreground placeholder:text-muted-foreground"
                />
            </div>

            {error && (
                <div className="bg-red-50 p-3 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                </div>
            )}

            <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleBack} disabled={loading}>
                    <ArrowLeft className="w-4 h-4 mr-1" /> Volver
                </Button>
                <Button onClick={handleApiSubmit} disabled={loading || !phoneNumber || !accessToken.trim()}>
                    {loading ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                    ) : (
                        'Conectar vía API'
                    )}
                </Button>
            </div>
        </div>
    );

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center">
                <div className="absolute inset-0 bg-black bg-opacity-30" onClick={handleClose} />
                <div className="relative bg-white rounded-2xl shadow-lg w-[90%] max-w-md p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-semibold text-lg text-foreground">
                            {connectionType === null
                                ? 'Agregar Canal de WhatsApp'
                                : connectionType === 'business'
                                    ? 'WhatsApp Business (QR)'
                                    : 'WhatsApp API (Token)'}
                        </h2>
                        <Button variant="ghost" className="text-foreground text-2xl" onClick={handleClose}>
                            ×
                        </Button>
                    </div>

                    {connectionType === null && renderTypeSelection()}
                    {connectionType === 'business' && renderBusinessForm()}
                    {connectionType === 'api' && renderApiForm()}
                </div>
            </div>

            {qrData && (
                <QRDisplayModal
                    open={showQRModal}
                    onClose={handleQRClose}
                    qrCode={qrData.qrCode}
                    instanceName={qrData.instanceName}
                    connectionId={qrData.connectionId}
                    onSuccess={handleQRSuccess}
                />
            )}
        </>
    );
};

export default NuevoChannelModal;
